import { CallAPI } from "../../apiManager/ApiCall";

const base = "payment/app-review-feedback";

const emptySummary = () => ({
  thumbs_up: 0,
  thumbs_down: 0,
  total: 0,
  by_platform: [],
});

function deriveSummaryFromRows(rows) {
  let thumbs_up = 0;
  let thumbs_down = 0;
  const map = {};
  for (const row of rows || []) {
    const sentiment = String(row?.sentiment || "")
      .trim()
      .toLowerCase();
    if (sentiment === "up") thumbs_up += 1;
    else if (sentiment === "down") thumbs_down += 1;
    const key =
      String(row?.platform || "unknown")
        .trim()
        .toLowerCase() || "unknown";
    if (!map[key]) map[key] = { platform: key, up: 0, down: 0, total: 0 };
    if (sentiment === "up") map[key].up += 1;
    else if (sentiment === "down") map[key].down += 1;
    map[key].total += 1;
  }
  return {
    thumbs_up,
    thumbs_down,
    total: thumbs_up + thumbs_down,
    by_platform: Object.values(map).sort((a, b) => b.total - a.total),
  };
}

/**
 * Prefer API thumbs_up / thumbs_down. If the API summary is missing/zero
 * (common when an older backend is deployed), derive from the row payload.
 */
function normalizeSummary(raw, rows = []) {
  const src = raw && typeof raw === "object" ? raw : {};
  const thumbs_up = Number(src.thumbs_up ?? src.thumbsUp ?? 0) || 0;
  const thumbs_down = Number(src.thumbs_down ?? src.thumbsDown ?? 0) || 0;
  const by_platform = Array.isArray(src.by_platform)
    ? src.by_platform
    : Array.isArray(src.byPlatform)
      ? src.byPlatform
      : [];

  if (thumbs_up > 0 || thumbs_down > 0) {
    return {
      thumbs_up,
      thumbs_down,
      total: thumbs_up + thumbs_down,
      by_platform,
    };
  }

  if (rows.length > 0) {
    return deriveSummaryFromRows(rows);
  }

  return emptySummary();
}

export const GetAppReviewFeedbackCall = async ({
  limit = 50,
  offset = 0,
} = {}) => {
  try {
    const q = new URLSearchParams();
    q.set("limit", String(limit));
    q.set("offset", String(offset));
    const response = await CallAPI({
      url: `${base}?${q.toString()}`,
      method: "GET",
    });
    if (response?.success && Array.isArray(response?.data)) {
      const rows = response.data;
      let summary = normalizeSummary(response.summary, rows);

      // If page summary is still empty/partial and there are more rows,
      // load up to 200 feedbacks just to compute accurate up/down totals.
      const listTotal =
        typeof response.total === "number" && response.total >= 0
          ? response.total
          : rows.length;
      if (
        (summary.total === 0 || summary.total < listTotal) &&
        listTotal > rows.length
      ) {
        try {
          const allQ = new URLSearchParams();
          allQ.set("limit", "200");
          allQ.set("offset", "0");
          const allRes = await CallAPI({
            url: `${base}?${allQ.toString()}`,
            method: "GET",
          });
          if (allRes?.success && Array.isArray(allRes.data) && allRes.data.length) {
            const fromAll = normalizeSummary(allRes.summary, allRes.data);
            if (fromAll.total > summary.total) {
              summary = fromAll;
            }
          }
        } catch (_) {
          /* keep page-derived summary */
        }
      }

      return {
        ok: true,
        rows,
        total: listTotal,
        summary,
        error: null,
      };
    }
    const message =
      (typeof response?.message === "string" && response.message) ||
      (response?.status === 401
        ? "Unauthorized — admin login required"
        : "Could not load app feedback");
    return {
      ok: false,
      rows: [],
      total: 0,
      summary: emptySummary(),
      error: message,
      status: response?.status || null,
    };
  } catch (err) {
    return {
      ok: false,
      rows: [],
      total: 0,
      summary: emptySummary(),
      error: err?.message || "Network error",
      status: null,
    };
  }
};

export const DeleteAppReviewFeedbackCall = async (id) => {
  try {
    const response = await CallAPI({
      url: `${base}/${encodeURIComponent(id)}`,
      method: "DELETE",
    });
    return { ok: response?.success === true, response };
  } catch {
    return { ok: false, response: null };
  }
};

export const BulkDeleteAppReviewFeedbackCall = async (ids) => {
  try {
    const response = await CallAPI({
      url: `${base}/bulk-delete`,
      method: "POST",
      body: { ids },
    });
    return {
      ok: response?.success === true,
      deleted: typeof response?.deleted === "number" ? response.deleted : 0,
      response,
    };
  } catch {
    return { ok: false, deleted: 0, response: null };
  }
};
